import { Box, TextField, Typography } from "@mui/material";
import { DragEvent, MouseEvent, PropsWithChildren, ReactNode, useState } from "react";
import { useHold } from "../../../misc/useHold";

// panelGutter is how far a panel holds its content off the edge, in px. Anything
// running the panel's full width lines its own text up with it.
export const panelGutter = 24;

// panelFontSize is the size of the text in a panel's rows, editors and notes, in px,
// so a checklist item, a comment and a description read as one piece
export const panelFontSize = 13;

// PanelSection is the frame the panel's lists share: the rows and whatever control
// adds a new one, under an optional label. A section inside a tab is already named by
// its tab, so it goes without.
export function PanelSection(props: PropsWithChildren<{
    label?: string;

    // noTopBorder drops the rule above the label, for a section that carries straight
    // on from whatever sits above it
    noTopBorder?: boolean;

    // action sits at the right-hand end of the label band, for the control that adds
    // a row. It needs a label to sit beside.
    action?: ReactNode;
}>) {
    return (
        // full-bleed: cancel the panel's horizontal padding so the separators run the
        // whole width. Each row gives it back so the text stays aligned.
        <Box sx={{ mx: -3 }}>
            {/* the label is a band rather than a line of text, so the sections are
                what the eye lands on first running down the panel */}
            {props.label && (
                <Box
                    sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 1, minHeight: 24,
                        px: 3, py: 0.75,
                        bgcolor: "grey.50",
                        borderTop: props.noTopBorder ? 0 : 1,
                        borderBottom: 1, borderColor: "divider",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        {props.label}
                    </Typography>
                    {props.action}
                </Box>
            )}
            {props.children}
        </Box>
    );
}

// PanelRow is one line of a PanelSection. Anything carrying the "panel-row-action"
// class only appears while the row is hovered, or always on a touch screen. Anything
// carrying "panel-row-hover-only" is left off a touch screen, where the row's menu
// offers the same action.
export function PanelRow(props: PropsWithChildren<{
    onContextMenu?: (e: MouseEvent) => void;

    // onHold runs on a touch and hold (see useHold)
    onHold?: (point: { x: number; y: number }) => void;

    // compact drops the gutter on the right, so the row's buttons reach the edge
    compact?: boolean;

    draggable?: boolean;
    onDragStart?: (e: DragEvent) => void;
    onDragEnd?: () => void;
    onDragOver?: (e: DragEvent) => void;
    onDrop?: (e: DragEvent) => void;
}>) {
    const hold = useHold({ onHold: props.onHold });

    return (
        <Box
            {...hold.handlers}
            onContextMenu={props.onContextMenu}
            draggable={props.draggable}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onDragOver={props.onDragOver}
            onDrop={props.onDrop}
            sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                py: 0.5, pl: 3, pr: props.compact ? 0 : 3,
                borderBottom: 1, borderColor: "divider",
                "&:hover": { bgcolor: "grey.50" },
                "&:hover .panel-row-action": { opacity: 1 },

                // a touch screen has no hover to show them with
                "@media (hover: none)": {
                    "& .panel-row-action": { opacity: 1 },
                    "& .panel-row-hover-only": { display: "none" },
                },

                // a touch and hold opens the menu rather than selecting the text
                ...(props.onHold ? { userSelect: "none", WebkitTouchCallout: "none" } : {}),
            }}
        >
            {props.children}
        </Box>
    );
}

// PanelAdd holds the control that adds a row, lined up with the rows above it. It
// sits on a tint so it reads as the end of the list rather than another row in it.
export function PanelAdd(props: PropsWithChildren<{}>) {
    return (
        <Box sx={{ px: 3, py: 0.5, bgcolor: "grey.50" }}>
            {props.children}
        </Box>
    );
}

// PanelAddField is how a row is added to a list: type a title, press enter, and the
// field is emptied and still focused for the next one, so a list is filled in one
// go. It stays live while a title is on its way to the server, since waiting on that
// would cost whatever was typed next.
export function PanelAddField(props: {
    placeholder: string;
    onAdd: (title: string) => void;
}) {
    const [title, setTitle] = useState("");

    return (
        <TextField
            variant="standard"
            fullWidth
            placeholder={props.placeholder}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => {
                if (e.key !== "Enter") return;
                e.preventDefault();

                const value = title.trim();
                if (!value) return;

                setTitle("");
                props.onAdd(value);
            }}
            slotProps={{
                input: { disableUnderline: true, style: { fontSize: panelFontSize } },
            }}
        />
    );
}
